<template>
  <div class="wrapper">
    <div
      class="star-bar rating-display"
      :title="
        (rating.value ?? null) === null
          ? `${rating.name} - not set`
          : `${rating.name} - ${rating.value}/${rating.max_stars ?? 5}`
      "
      @click.left.prevent="moveState(true)"
      @click.right.prevent="moveState(false)"
      @click.middle.prevent="resetState()"
    >
      <SvgSymbol
        v-for="index in (rating.max_stars ?? 5)"
        :key="index"
        :symbol="
          (rating.value ?? null) === null
            ? rated.null.name // null
            : index <= rating.value
            ? rated.true.name // below rating
            : rated.false.name // above rating
        "
        :color="
          (rating.value ?? null) === null
            ? rated.null.color // probably null
            : index <= rating.value
            ? rated.true.color
            : rated.false.color
        "
      />
    </div>
    <PlusMinus
      v-if="showButtons"
      class="plus-minus"
      @increase="moveState(true)"
      @decrease="moveState(false)"
      @reset="resetState()"
    />
  </div>
</template>

<script setup lang="ts">
import type {
  StarsServiceIncludingValue,
  StarsValue,
} from "@/stores/ratingService";
import SvgSymbol from "@/components/Svg/SvgSymbol.vue";
import { computed, defineEmits, defineProps, withDefaults } from "vue";
import { byApiName } from "@/components/HydrusRating/Symbols";
import type { RatingStatus } from "@/components/HydrusRating/Symbols";
import PlusMinus from "@/components/HydrusRating/PlusMinus.vue";

const props = withDefaults(
  defineProps<{
    rating: StarsServiceIncludingValue,
    showButtons: boolean,
  }>(),
  {
    showButtons: true,
  },
);

const emit = defineEmits<{
  (event: "set", newState: StarsValue, oldState: StarsValue): void;
}>();

const rated = computed(
  () => {
    // Guard against missing star_shape (defensive programming)
    if (!props.rating.star_shape) {
      return byApiName.circle;
    }
    // eslint-disable-next-line prettier/prettier
    return byApiName[
      props.rating.star_shape
    ] ?? byApiName.circle;
  }
);

const index = (props.rating.value ?? "null").toString() as RatingStatus;

const neutralState = null;

function moveState(up: boolean) {
  // pull current value or neutral/default
  let newState = props.rating.value ?? neutralState;
  const minStars = props.rating.min_stars ?? 0;
  const maxStars = props.rating.max_stars ?? 5;
  // increment it as needed
  // - also make sure it stays in bounds (null, min_stars, … max_stars)
  if (newState === neutralState) {
    if (up) {
      newState = minStars;
    }
    // else: stay at neutralState.
  } else {
    newState += up ? 1 : -1;
    if (newState < minStars) {
      newState = neutralState;
    } else {
      newState = Math.min(newState, maxStars);
    }
  }
  // emit the new value (and the old value)
  emit("set", newState, props.rating.value);
}

function resetState() {
  emit("set", neutralState, props.rating.value);
}
</script>

<style scoped>
.wrapper {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;

  .rating-display, .plus-minus {
    flex-shrink: 0;
  }
  .rating-display {
    flex-grow: 1;
    display: flex;
    align-content: center;
    justify-content: center;
  }
  .plus-minus {
    flex-grow: 0;
    margin-left: 1em;
  }
}
</style>
